import apiClient from './apiClient'
import { ENDPOINTS } from './endpoints'

export interface AcademicSessionDto {
  id: string
  orgId?: string
  branchId?: string | null
  academicYearName: string
  startDate?: string
  endDate?: string
  isActive?: boolean
  isOrgDefault?: boolean
  enrolledStudentCount?: number
}

function unwrapResponse<T>(data: any): T {
  if (data && typeof data === 'object' && 'data' in data && data.data !== undefined) {
    return data.data as T
  }
  return data as T
}

/**
 * Fetches the current active academic session for the caller via GET /api/academic-sessions/current.
 * Automatically stores the session ID and name in LocalStorage and returns the session DTO.
 */
export const getCurrentAcademicSession = async (): Promise<AcademicSessionDto | null> => {
  try {
    const response = await apiClient.get<any>(ENDPOINTS.ACADEMIC_SESSIONS.GET_CURRENT)
    const rawData = unwrapResponse<any>(response.data)
    const sessionObj = rawData?.data || rawData
    if (sessionObj && (sessionObj.id || sessionObj.academicYearName)) {
      const sessionId = sessionObj.id || sessionObj.sessionId || ''
      const sessionName = sessionObj.academicYearName || sessionObj.name || '2026-2027'
      if (typeof window !== 'undefined' && sessionId) {
        localStorage.setItem('academic_year_id', sessionId)
        localStorage.setItem('academic_session_id', sessionId)
        localStorage.setItem('x_academic_year', sessionId)
        localStorage.setItem('academic_year_name', sessionName)
      }
      return {
        id: sessionId,
        academicYearName: sessionName,
        ...sessionObj,
      }
    }
  } catch (err: any) {
    console.warn('[AcademicYearResolver] GET /api/academic-sessions/current fallback:', err?.message)
  }
  return null
}

export interface AcademicSessionOption {
  id: string
  name: string
  label: string
  value: string
  isCurrent?: boolean
  isActive?: boolean
}

/**
 * Fetches academic session options for selection dropdowns via GET /api/academic-sessions/options.
 * Falls back to GET /api/academic-sessions if /options endpoint is not available.
 */
export const getAcademicSessionOptions = async (): Promise<AcademicSessionOption[]> => {
  try {
    const response = await apiClient.get<any>(ENDPOINTS.ACADEMIC_SESSIONS.OPTIONS)
    const payload = response.data
    const dataObj = payload?.data || payload
    const items = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.items)
      ? payload.items
      : Array.isArray(dataObj?.items)
      ? dataObj.items
      : Array.isArray(dataObj)
      ? dataObj
      : []

    if (items.length > 0) {
      return items.map((item: any) => {
        const id = item.id || item.value || item.sessionId || ''
        const name = item.academicYearName || item.sessionName || item.name || item.label || item.title || item.year || '2026-2027'
        return {
          id,
          name,
          label: item.label || name,
          value: id,
          isCurrent: Boolean(item.isCurrent || item.isOrgDefault),
          isActive: item.isActive !== false,
        }
      })
    }
  } catch (err: any) {
    console.warn('[AcademicYearResolver] GET /api/academic-sessions/options fallback:', err?.message)
  }

  // Fallback to GET /api/academic-sessions
  try {
    const response = await apiClient.get<any>(ENDPOINTS.ACADEMIC_SESSIONS.SESSIONS, {
      params: { limit: 100 },
    })
    const data = unwrapResponse<any>(response.data)
    const items: AcademicSessionDto[] = Array.isArray(data)
      ? data
      : Array.isArray(data?.items)
      ? data.items
      : []

    return items.map((item: any) => {
      const id = item.id || item.value || item.sessionId || ''
      const name = item.academicYearName || item.sessionName || item.name || item.label || item.title || item.year || '2026-2027'
      return {
        id,
        name,
        label: item.label || name,
        value: id,
        isCurrent: Boolean(item.isCurrent || item.isOrgDefault),
        isActive: item.isActive !== false,
      }
    })
  } catch (err: any) {
    console.warn('[AcademicYearResolver] GET /api/academic-sessions fallback:', err?.message)
    return []
  }
}

/**
 * Dynamically resolves the appropriate academic session UUID
 * from the live /api/academic-sessions endpoint or /api/academic-sessions/current.
 *
 * Rules:
 * 1. Priority 1 — Explicit Admin Selection (managed_academic_year):
 *    - Resolves the Admin's authoritative selected year against live backend sessions.
 * 2. Priority 2 — Current Branch / Organization Default:
 *    - Prefers active default session (isOrgDefault === true) for current branch or org.
 * 3. Priority 3 — Active Operational Session:
 *    - Prefers strictly active sessions with enrolled students or classes (enrolledStudentCount > 0 or classCount > 0).
 * 4. Priority 4 — Date-Range Fallback:
 *    - Matches active session covering target date (startDate <= targetDate <= endDate).
 * 5. Priority 5 — Final Active Session Fallback:
 *    - Takes first active session in pool.
 *
 * Automatically synchronizes runtime transport caches (academic_year_id & x_academic_year).
 *
 * @param targetDate Optional target date string in 'YYYY-MM-DD' format (e.g. '2026-09-08').
 */
export const getActiveAcademicYearId = async (targetDate?: string): Promise<string> => {
  try {
    const currentSession = await getCurrentAcademicSession()
    if (currentSession?.id) {
      return currentSession.id
    }
  } catch {
    // proceed to sessions list fallback
  }

  const existingAcademicYear =
    localStorage.getItem('x_academic_year') ||
    localStorage.getItem('academic_year_id')

  // 1. Resolve current branch ID from application context
  let currentBranchId =
    localStorage.getItem('branch_id') ||
    localStorage.getItem('x_branch_id') ||
    sessionStorage.getItem('branch_id') ||
    ''

  let userOrgId = ''
  if (!currentBranchId) {
    try {
      const storedUser = JSON.parse(localStorage.getItem('school-erp-user') || '{}')
      if (storedUser?.branchId) {
        currentBranchId = storedUser.branchId
      }
      if (storedUser?.organizationId || storedUser?.orgId) {
        userOrgId = storedUser?.organizationId || storedUser?.orgId
      }
    } catch {
      // ignore
    }
  } else {
    try {
      const storedUser = JSON.parse(localStorage.getItem('school-erp-user') || '{}')
      if (storedUser?.organizationId || storedUser?.orgId) {
        userOrgId = storedUser?.organizationId || storedUser?.orgId
      }
    } catch {
      // ignore
    }
  }

  // 2. Determine target date string (defaults to current date if not provided)
  const dateStr = targetDate ? targetDate.split('T')[0] : new Date().toISOString().split('T')[0]

  const managedYearName = localStorage.getItem('managed_academic_year')?.trim() || ''

  console.log('[AcademicYearResolver] 1. Storage academic-year values:', {
    managedYearName,
    branch_id: currentBranchId || 'none',
  })
  console.log('[AcademicYearResolver] 2. Target Attendance Date:', dateStr)

  // 3. Fetch live academic sessions from backend without header interference
  try {
    const response = await apiClient.get<any>(ENDPOINTS.ACADEMIC_SESSIONS.SESSIONS, {
      params: { limit: 100 },
    })
    const data = unwrapResponse<any>(response.data)
    const rawSessions: AcademicSessionDto[] = Array.isArray(data)
      ? data
      : Array.isArray(data?.items)
      ? data.items
      : []

    const sessions = userOrgId
      ? rawSessions.filter((s: any) => {
          const itemOrgId = s.orgId || s.organizationId
          return !itemOrgId || itemOrgId === userOrgId
        })
      : rawSessions

    if (sessions.length === 0) {
      console.warn('[AcademicYearResolver] GET /api/academic-sessions returned 0 sessions.')
      return existingAcademicYear || ''
    }

    // Clean up stale existingAcademicYear if invalid
    if (existingAcademicYear && existingAcademicYear.trim()) {
      const validActiveSession = sessions.find(
        (s) => s.id === existingAcademicYear.trim() && s.isActive !== false
      )
      if (!validActiveSession) {
        console.warn('[AcademicYearResolver] Stale/invalid academic year found in localStorage, clearing:', existingAcademicYear)
        localStorage.removeItem('academic_year_id')
        localStorage.removeItem('x_academic_year')
        localStorage.removeItem('academic_session_id')
        sessionStorage.removeItem('academic_year_id')
        sessionStorage.removeItem('x_academic_year')
        sessionStorage.removeItem('academic_session_id')
      }
    }

    console.log('[AcademicYearResolver] 3. All Candidate Sessions from Backend:', sessions.map((s) => ({
      id: s.id,
      academicYearName: s.academicYearName,
      branchId: s.branchId,
      isActive: s.isActive,
      startDate: s.startDate,
      endDate: s.endDate,
      enrolledStudentCount: s.enrolledStudentCount,
      isOrgDefault: s.isOrgDefault,
    })))

    // Helper: check if dateStr falls within session startDate and endDate
    const isDateInRange = (s: AcademicSessionDto, d: string): boolean => {
      if (!s.startDate || !s.endDate) return false
      const start = s.startDate.split('T')[0]
      const end = s.endDate.split('T')[0]
      return start <= d && d <= end
    }

    // Filter strictly for ACTIVE sessions first
    const activeSessions = sessions.filter((s) => s.isActive === true || s.isActive !== false)

    // Candidate pool: prefer active sessions pool if available, otherwise full sessions
    const pool = activeSessions.length > 0 ? activeSessions : sessions

    let selectedSession: AcademicSessionDto | undefined

    // -------------------------------------------------------------
    // PRIORITY 1: EXPLICIT ADMIN SELECTION (managed_academic_year)
    // -------------------------------------------------------------
    if (managedYearName) {
      if (currentBranchId) {
        selectedSession =
          pool.find((s) => s.branchId === currentBranchId && (s.academicYearName === managedYearName || s.academicYearName?.startsWith(managedYearName))) ||
          sessions.find((s) => s.branchId === currentBranchId && (s.academicYearName === managedYearName || s.academicYearName?.startsWith(managedYearName)))
      }
      if (!selectedSession) {
        selectedSession =
          pool.find((s) => s.academicYearName === managedYearName || s.academicYearName?.startsWith(managedYearName)) ||
          sessions.find((s) => s.academicYearName === managedYearName || s.academicYearName?.startsWith(managedYearName))
      }
    }

    // -------------------------------------------------------------
    // PRIORITY 2: CURRENT BRANCH / ORGANIZATION DEFAULT (isOrgDefault === true)
    // -------------------------------------------------------------
    if (!selectedSession) {
      // 2A. Branch-specific default session
      if (currentBranchId) {
        selectedSession = pool.find((s) => s.branchId === currentBranchId && s.isOrgDefault === true)
      }
      // 2B. Org-level default session (not bound to another branch)
      if (!selectedSession) {
        selectedSession = pool.find((s) => (!s.branchId || (currentBranchId && s.branchId === currentBranchId)) && s.isOrgDefault === true)
      }
      // 2C. Any default session in pool
      if (!selectedSession) {
        selectedSession = pool.find((s) => s.isOrgDefault === true)
      }
    }

    // -------------------------------------------------------------
    // PRIORITY 3: ACTIVE OPERATIONAL SESSION (enrolledStudentCount > 0 OR classCount > 0)
    // -------------------------------------------------------------
    if (!selectedSession) {
      const isPopulated = (s: AcademicSessionDto) =>
        s.isActive === true && ((s.enrolledStudentCount || 0) > 0 || ((s as any).classCount || 0) > 0 || ((s as any).sectionCount || 0) > 0)

      // 3A. Active branch populated session
      if (currentBranchId) {
        selectedSession = pool.find((s) => s.branchId === currentBranchId && isPopulated(s))
      }
      // 3B. Any active populated session
      if (!selectedSession) {
        selectedSession = pool.find((s) => isPopulated(s))
      }
    }

    // -------------------------------------------------------------
    // PRIORITY 4: DATE-RANGE FALLBACK (startDate <= targetDate <= endDate)
    // -------------------------------------------------------------
    if (!selectedSession) {
      // 4A. Branch date-range match
      if (currentBranchId) {
        selectedSession = pool.find((s) => s.branchId === currentBranchId && isDateInRange(s, dateStr))
      }
      // 4B. Org-level or any date-range match
      if (!selectedSession) {
        selectedSession = pool.find((s) => isDateInRange(s, dateStr))
      }
    }

    // -------------------------------------------------------------
    // PRIORITY 5: FINAL ACTIVE SESSION FALLBACK
    // -------------------------------------------------------------
    if (!selectedSession) {
      if (currentBranchId) {
        selectedSession = pool.find((s) => s.branchId === currentBranchId && s.isActive === true)
      }
      if (!selectedSession) {
        selectedSession = pool.find((s) => s.isActive === true) || pool[0]
      }
    }

    if (selectedSession) {
      console.log('[AcademicYearResolver] 4. Selected Session:', {
        id: selectedSession.id,
        academicYearName: selectedSession.academicYearName,
        isActive: selectedSession.isActive,
        branchId: selectedSession.branchId,
        startDate: selectedSession.startDate,
        endDate: selectedSession.endDate,
        enrolledStudentCount: selectedSession.enrolledStudentCount,
      })

      // Synchronize runtime transport caches with the resolved session UUID
      localStorage.setItem('academic_year_id', selectedSession.id)
      localStorage.setItem('x_academic_year', selectedSession.id)
      return selectedSession.id
    }

    console.warn('[AcademicYearResolver] No suitable academic session could be resolved.')
    return ''
  } catch (err: any) {
    console.warn('[AcademicYearResolver] GET /api/academic-sessions error:', err?.response?.status || err?.message)
    // If the user is non-admin/staff and gets 403 Forbidden on /api/academic-sessions,
    // clear any stale cached academic session ID so we don't send an invalid X-Academic-Year header
    if (err?.response?.status === 403 || err?.response?.status === 401) {
      const activeFallback = existingAcademicYear || ''
      console.warn('[AcademicYearResolver] 403/401 on academic-sessions. Preserving active academic year:', activeFallback)
      if (activeFallback) {
        localStorage.setItem('academic_year_id', activeFallback)
        localStorage.setItem('x_academic_year', activeFallback)
      }
      return activeFallback
    }
    return existingAcademicYear || ''
  }
}
